import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import { StatusBadge, ConfirmModal } from "@/pages/admin/AdminPortfolio";

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    api.get("/admin/blog").then((r) => setItems(r.data)).catch(() => toast.error("Failed to load posts")).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () =>
      items.filter(
        (p) =>
          (status === "all" || p.status === status) &&
          (p.title.toLowerCase().includes(q.toLowerCase()) || (p.category || "").toLowerCase().includes(q.toLowerCase()))
      ),
    [items, q, status]
  );

  const toggleStatus = async (p) => {
    const next = p.status === "published" ? "draft" : "published";
    try {
      await api.patch(`/admin/blog/${p.id}/status`, { status: next });
      setItems((prev) => prev.map((x) => (x.id === p.id ? { ...x, status: next } : x)));
      toast.success(next === "published" ? "Post published" : "Post unpublished");
    } catch {
      toast.error("Could not update status");
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/blog/${deleteTarget.id}`);
      setItems((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      toast.success("Post deleted");
    } catch {
      toast.error("Could not delete post");
    }
    setDeleteTarget(null);
  };

  return (
    <div data-testid="admin-blog-page">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight md:text-3xl">Blog</h1>
          <p className="mt-1 text-sm text-neutral-500">{items.length} posts total</p>
        </div>
        <Link to="/admin/blog/new" className="btn-primary !px-5 !py-2.5 text-xs" data-testid="new-post-button">
          <Plus className="h-4 w-4" /> New Post
        </Link>
      </div>

      <div className="mb-5 flex flex-wrap gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search posts…"
          className="input-glass !w-auto min-w-[200px] flex-1 !py-2.5"
          data-testid="blog-search-input"
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="input-glass !w-auto !py-2.5" data-testid="blog-status-filter">
          <option value="all">All status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
      </div>

      <div className="glass overflow-x-auto rounded-2xl">
        <table className="w-full min-w-[680px] text-left text-sm" data-testid="blog-table">
          <thead>
            <tr className="border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              <th className="px-5 py-4">Post</th>
              <th className="px-5 py-4">Category</th>
              <th className="px-5 py-4">Status</th>
              <th className="px-5 py-4">Date</th>
              <th className="px-5 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-5 py-10 text-center text-neutral-500">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-neutral-500" data-testid="blog-empty">No posts match your filters.</td></tr>
            )}
            {filtered.map((p) => (
              <tr key={p.id} className="border-b border-white/[0.05] transition-colors last:border-0 hover:bg-white/[0.02]" data-testid={`blog-row-${p.slug}`}>
                <td className="px-5 py-4">
                  <div className="flex items-center gap-4">
                    {p.featured_image && (
                      <img src={mediaUrl(p.featured_image)} alt="" className="h-11 w-16 rounded-lg border border-white/10 object-cover" />
                    )}
                    <div>
                      <p className="font-semibold text-white">{p.title}</p>
                      <p className="line-clamp-1 max-w-[260px] text-xs text-neutral-500">{p.excerpt}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-4 text-neutral-400">{p.category}</td>
                <td className="px-5 py-4"><StatusBadge status={p.status} /></td>
                <td className="px-5 py-4 font-mono text-xs text-neutral-500">{(p.published_at || p.created_at || "").slice(0, 10)}</td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-2">
                    <Link to={`/admin/blog/${p.id}/edit`} data-testid={`edit-post-${p.slug}`} aria-label={`Edit ${p.title}`} className="glass flex h-9 w-9 items-center justify-center rounded-full text-neutral-400 transition-colors hover:text-white">
                      <Pencil className="h-4 w-4" />
                    </Link>
                    <button onClick={() => toggleStatus(p)} data-testid={`toggle-post-${p.slug}`} aria-label="Toggle status" className="glass flex h-9 w-9 items-center justify-center rounded-full text-neutral-400 transition-colors hover:text-white">
                      {p.status === "published" ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button onClick={() => setDeleteTarget(p)} data-testid={`delete-post-${p.slug}`} aria-label={`Delete ${p.title}`} className="glass flex h-9 w-9 items-center justify-center rounded-full text-neutral-400 transition-colors hover:text-red-300">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmModal target={deleteTarget} noun="post" onCancel={() => setDeleteTarget(null)} onConfirm={confirmDelete} />
    </div>
  );
}
